import { useEffect, useRef, useState } from 'react';
import {
  Smile, Edit2, Trash2, Check, CheckCheck,
  ListTodo, MoreVertical, CheckCircle2, MessageSquare, Pin,
} from 'lucide-react';
import type { Message, Reaction } from '../../../api/chat';
import Avatar from './Avatar';
import AttachmentBubble from './AttachmentBubble';
import { EMOJIS, fmtTime, isEditableNow, type PendingMessage } from '../utils/format';

function buildHtml(text: string, currentUserName?: string): string {
  if (!text) return '';
  let out = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  // Bold **text** or __text__
  out = out.replace(/\*\*(.+?)\*\*/gs, '<strong>$1</strong>');
  out = out.replace(/__(.+?)__/gs, '<strong>$1</strong>');
  // Italic *text* or _text_ (single, after bold)
  out = out.replace(/\*([^*\n]+?)\*/g, '<em>$1</em>');
  out = out.replace(/_([^_\n]+?)_/g, '<em>$1</em>');
  // Inline code `text`
  out = out.replace(/`([^`\n]+?)`/g,
    '<code style="background:#f3f4f6;padding:1px 5px;border-radius:4px;font-family:ui-monospace,monospace;font-size:0.85em;">$1</code>');
  // Strikethrough ~~text~~
  out = out.replace(/~~(.+?)~~/gs, '<del>$1</del>');
  // URLs
  out = out.replace(/(https?:\/\/[^\s&<]+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer" style="text-decoration:underline;opacity:0.85;">$1</a>');
  // Newlines → <br>
  out = out.replace(/\n/g, '<br>');
  // @mentions
  const myName = (currentUserName || '').trim().toLowerCase();
  out = out.replace(/@([^\s<&;]+(?:\s[^\s<&;]+)?)/g, (_m, name) => {
    const mentioned = name.trim().toLowerCase();
    const isSelf = myName.length > 0 && (
      mentioned === myName ||
      mentioned.startsWith(myName + ' ') ||
      myName.startsWith(mentioned + ' ') ||
      mentioned === myName.split(' ')[0]
    );
    const style = isSelf
      ? 'style="background:#fef9c3;color:#854d0e;padding:0 4px;border-radius:3px;font-weight:600;"'
      : 'style="background:#dbeafe;color:#1d4ed8;padding:0 4px;border-radius:3px;font-weight:600;"';
    return `<span ${style}>@${name}</span>`;
  });
  return out;
}

function MessageBubble({
  msg, isMine, seenByOther, highlighted, isPinned, onReact, onEdit, onDelete, onTask, onTogglePin, onThreadOpen, onSenderClick, senderSubtitle, currentUserName,
  isFirstInGroup = true, isLastInGroup = true,
}: {
  msg: PendingMessage;
  isMine: boolean;
  seenByOther?: boolean;
  highlighted?: boolean;
  isPinned?: boolean;
  onReact: (id: string, emoji: string) => void;
  onEdit: (msg: Message) => void;
  onDelete: (id: string) => void;
  onTask: (msg: Message) => void;
  onTogglePin?: (msg: Message) => void;
  onThreadOpen?: (msg: Message) => void;
  onSenderClick?: (senderId: string) => void;
  senderSubtitle?: string;
  currentUserName?: string;
  isFirstInGroup?: boolean;
  isLastInGroup?: boolean;
}) {
  const [showEmoji, setShowEmoji] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [threadPulse, setThreadPulse] = useState(false);
  const prevReplyCountRef = useRef<number | undefined>(undefined);
  const menuRef = useRef<HTMLDivElement>(null);

  const replyCountForEffect = (msg as any).reply_count as number | undefined;
  useEffect(() => {
    const prev = prevReplyCountRef.current;
    prevReplyCountRef.current = replyCountForEffect;
    if (replyCountForEffect && prev !== undefined && replyCountForEffect > prev) {
      setThreadPulse(true);
      const t = setTimeout(() => setThreadPulse(false), 1500);
      return () => clearTimeout(t);
    }
  }, [replyCountForEffect]);

  useEffect(() => {
    if (!showMenu && !showEmoji) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false); setShowEmoji(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMenu, showEmoji]);

  if (msg.is_deleted) {
    return (
      <div className={`flex ${isMine ? 'justify-end' : 'justify-start'} mb-1 px-4`}>
        <span className="text-xs text-gray-300 italic border border-gray-200 rounded-xl px-3 py-1.5 bg-white">
          🚫 This message was deleted
        </span>
      </div>
    );
  }

  if (msg.message_type === 'system') {
    return (
      <div className="flex justify-center my-3 px-4">
        <span className="text-xs text-gray-500 bg-gray-100/80 backdrop-blur px-4 py-1.5 rounded-full border border-gray-200">
          {msg.content}
        </span>
      </div>
    );
  }

  if (msg.message_type === 'task_link') {
    return (
      <div className="flex justify-center my-2 px-4">
        <span className="text-xs text-blue-700 bg-blue-50 border border-blue-100 px-4 py-1.5 rounded-full flex items-center gap-2 shadow-sm">
          <CheckCircle2 size={12} className="text-blue-500" /> {msg.content}
        </span>
      </div>
    );
  }

  const att = msg.attachments?.[0];
  const reactions = (msg.reactions || []) as Reaction[];
  const replyCount = (msg as any).reply_count as number | undefined;

  const groupSpacing = isLastInGroup ? 'mb-1' : 'mb-0.5';
  // Only show the "tail" on the first bubble of a group (Slack-style).
  const tailOn = isFirstInGroup && isLastInGroup;
  const mineCorner = tailOn ? 'rounded-br-sm' : '';
  const otherCorner = tailOn ? 'rounded-bl-sm' : '';

  return (
    <div
      id={`msg-${msg.id}`}
      onDoubleClick={() => { if (!msg._pending && !msg.is_deleted) onReact(msg.id, '👍'); }}
      className={`flex ${isMine ? 'flex-row-reverse' : 'flex-row'} gap-2 ${groupSpacing} px-4 group transition-colors ${highlighted ? 'bg-yellow-100' : ''}`}
    >
      {/* Avatar — only for first message in a group; spacer otherwise so bubbles align. */}
      {!isMine && (
        isFirstInGroup ? (
          <div className="mt-auto mb-5 flex-shrink-0">
            <Avatar name={msg.sender_name} size={8} />
          </div>
        ) : (
          <div className="w-8 flex-shrink-0" />
        )
      )}

      {/* Bubble + meta */}
      <div className={`max-w-[65%] flex flex-col ${isMine ? 'items-end' : 'items-start'}`}>
        {!isMine && isFirstInGroup && (
          <button
            type="button"
            onClick={() => onSenderClick?.(msg.sender_id)}
            className="mb-1 ml-1 text-left hover:text-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 rounded">
            <span className="block text-xs font-semibold text-gray-900 hover:underline">
              {msg.sender_name || 'Unknown'}
            </span>
            {senderSubtitle && (
              <span className="block text-[11px] text-gray-500">
                {senderSubtitle}
              </span>
            )}
          </button>
        )}

        <div
          className={`relative rounded-2xl px-4 py-3 shadow-sm ${
            isMine
              ? msg._pending
                ? `bg-blue-500 text-white ${mineCorner} opacity-80`
                : `bg-blue-600 text-white ${mineCorner}`
              : `bg-white text-gray-900 ${otherCorner} border border-gray-200`
          }`}
        >
          {att ? (
            <AttachmentBubble att={att} isMine={isMine} />
          ) : (
            <p
            className="text-[13.5px] leading-relaxed break-words"
            dangerouslySetInnerHTML={{ __html: buildHtml(msg.content || '', currentUserName) }}
          />
          )}

          {msg.is_edited && (
            <span className={`text-[11px] ${isMine ? 'text-blue-100' : 'text-gray-500'} mt-0.5 block`}>(edited)</span>
          )}

          {isPinned && (
            <span
              className="absolute -top-2 -right-2 bg-amber-100 text-amber-700 rounded-full px-1.5 py-0.5 text-[10px] flex items-center gap-0.5 shadow-sm border border-amber-200"
              title="Pinned"
            >
              <Pin size={10} />
              <span className="font-semibold">Pinned</span>
            </span>
          )}
        </div>

        {/* Thread reply count chip — hidden entirely when count is 0 */}
        {onThreadOpen && typeof replyCount === 'number' && replyCount >= 1 && !msg.thread_parent_id && (
          <button
            type="button"
            onClick={() => onThreadOpen(msg)}
            className={`flex items-center gap-1.5 mt-1 px-2.5 py-1 text-[11px] font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-100 rounded-full transition-colors ${isMine ? 'self-end' : 'self-start'} ${threadPulse ? 'animate-pulse ring-2 ring-blue-300' : ''}`}
          >
            <MessageSquare size={11} />
            <span>{replyCount} {replyCount === 1 ? 'reply' : 'replies'}</span>
          </button>
        )}

        {/* Reactions */}
        {reactions.filter(r => r.count > 0).length > 0 && (
          <div className={`flex flex-wrap gap-1 mt-1 ${isMine ? 'justify-end' : ''}`}>
            {reactions.filter(r => r.count > 0).map(r => (
              <button key={r.emoji} onClick={() => onReact(msg.id, r.emoji)}
                className="flex items-center gap-1 bg-white border border-gray-200 hover:border-blue-600 hover:bg-blue-50 shadow-sm rounded-full px-2 py-0.5 text-xs transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500">
                <span>{r.emoji}</span>
                <span className="text-gray-600 font-medium">{r.count}</span>
              </button>
            ))}
          </div>
        )}

        {/* Time + status — only on the last message of a group (Slack-style). */}
        {isLastInGroup && (
          <div className={`flex items-center gap-1 mt-0.5 ${isMine ? 'flex-row-reverse' : ''}`}>
            <span className="text-[11px] text-gray-500">{fmtTime(msg.created_at)}</span>
            {isMine && (
              msg._pending
                ? <Check size={11} className="text-gray-400" />
                : seenByOther
                  ? <CheckCheck size={11} className="text-blue-600" />
                  : <CheckCheck size={11} className="text-gray-400" />
            )}
          </div>
        )}
      </div>

      {/* Floating action bar */}
      <div
        ref={menuRef}
        className={`self-center flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity ${isMine ? 'mr-1' : 'ml-1'}`}
      >
        {!msg._pending && (
          <div className="flex items-center gap-0.5 bg-white border border-gray-200 rounded-xl shadow-md px-1 py-0.5">
            {/* Quick emoji */}
            {['👍', '❤️', '😂'].map(e => (
              <button key={e} onClick={() => onReact(msg.id, e)}
                className="p-1 hover:bg-gray-100 rounded-lg text-base transition-transform hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-blue-500">
                {e}
              </button>
            ))}

            {/* Emoji picker */}
            <div className="relative">
              <button onClick={() => { setShowEmoji(v => !v); setShowMenu(false); }}
                className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-600 hover:text-gray-900 transition-colors hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500">
                <Smile size={14} />
              </button>
              {showEmoji && (
                <div className={`absolute bottom-9 ${isMine ? 'right-0' : 'left-0'} bg-white border border-gray-200 rounded-xl shadow-xl p-2.5 flex gap-1.5 z-30 whitespace-nowrap`}>
                  {EMOJIS.map(e => (
                    <button key={e} onClick={() => { onReact(msg.id, e); setShowEmoji(false); }}
                      className="text-xl hover:scale-105 transition-transform active:scale-95">{e}</button>
                  ))}
                </div>
              )}
            </div>

            {/* More menu */}
            <div className="relative">
              <button onClick={() => { setShowMenu(v => !v); setShowEmoji(false); }}
                className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-600 hover:text-gray-900 transition-colors hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500">
                <MoreVertical size={14} />
              </button>
              {showMenu && (
                <div className={`absolute bottom-9 ${isMine ? 'right-0' : 'left-0'} bg-white border border-gray-200 rounded-xl shadow-xl py-1 z-30 w-44 overflow-hidden`}>
                  {onThreadOpen && !msg.thread_parent_id && (
                    <button onClick={() => { onThreadOpen(msg); setShowMenu(false); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center gap-2.5 text-gray-900 transition-colors">
                      <MessageSquare size={14} className="text-blue-600" /> Reply in thread
                    </button>
                  )}
                  <button onClick={() => { onTask(msg); setShowMenu(false); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center gap-2.5 text-gray-900 transition-colors">
                    <ListTodo size={14} className="text-blue-600" /> Create Task
                  </button>
                  {onTogglePin && (
                    <button onClick={() => { onTogglePin(msg); setShowMenu(false); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center gap-2.5 text-gray-900 transition-colors">
                      <Pin size={14} className="text-amber-500" /> {isPinned ? 'Unpin' : 'Pin to conversation'}
                    </button>
                  )}
                  {isMine && (
                    <>
                      <div className="h-px bg-gray-200 my-1" />
                      {isMine && isEditableNow(msg.created_at) && (
                      <button onClick={() => { onEdit(msg); setShowMenu(false); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center gap-2.5 text-gray-900 transition-colors">
                        <Edit2 size={14} className="text-gray-600" /> Edit Message
                      </button>
                      )}
                      <button onClick={() => { onDelete(msg.id); setShowMenu(false); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-red-50 flex items-center gap-2.5 text-red-600 transition-colors">
                        <Trash2 size={14} /> Delete Message
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MessageBubble;
